import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable, catchError, of, shareReplay } from 'rxjs';
import { environment } from '@env/environment';
import {
  getSettingArray,
  getSettingObject,
  getSettingText,
  normalizeExternalUrl,
  resolveAssetUrl,
} from '@app/utils/site-setting-value.util';
import { SiteSettingValue } from '@app/interfaces/common/site-setting.interfaces';
import type { SiteSettingItem } from '@app/interfaces/common/site-setting.interfaces';

export type { SiteSettingItem } from '@app/interfaces/common/site-setting.interfaces';

@Injectable({ providedIn: 'root' })
export class SiteSettingService {
  private readonly publicSettingsUrl = `${environment.apiUrl}/public/settings`;
  private readonly adminSettingsUrl = `${environment.apiUrl}/admin/settings`;
  private publicSettings$?: Observable<Record<string, SiteSettingValue>>;
  private publicSettingsWarmupStarted = false;

  constructor(private http: HttpClient) {}

  getPublicSettings(forceRefresh = false): Observable<Record<string, SiteSettingValue>> {
    if (forceRefresh) {
      this.clearPublicSettingsCache();
    }
    if (!this.publicSettings$) {
      this.publicSettings$ = this.http
        .get<Record<string, SiteSettingValue>>(this.publicSettingsUrl)
        .pipe(
          catchError(() => of({} as Record<string, SiteSettingValue>)),
          shareReplay(1),
        );
    }
    return this.publicSettings$;
  }

  warmPublicSettingsCache(): void {
    if (this.publicSettingsWarmupStarted || typeof window === 'undefined') {
      return;
    }

    this.publicSettingsWarmupStarted = true;
    this.getPublicSettings().subscribe();
  }

  clearPublicSettingsCache(): void {
    this.publicSettings$ = undefined;
    this.publicSettingsWarmupStarted = false;
  }

  getText(settings: Record<string, SiteSettingValue>, key: string, fallback: string): string {
    return getSettingText(settings[key], fallback);
  }

  getArray<T>(settings: Record<string, SiteSettingValue>, key: string, fallback: T[]): T[] {
    return getSettingArray<T>(settings[key], fallback);
  }

  getObject<T>(settings: Record<string, SiteSettingValue>, key: string, fallback: T): T {
    return getSettingObject<T>(settings[key], fallback);
  }

  resolveAssetUrl(path: unknown, fallback: string = ''): string {
    return resolveAssetUrl(path, fallback);
  }

  normalizeLinkUrl(url: unknown, fallback: string = '#'): string {
    return normalizeExternalUrl(url, fallback);
  }

  getAdminSettings(): Observable<{
    all: SiteSettingItem[];
    grouped: Record<string, SiteSettingItem[]>;
  }> {
    return this.http.get<{ all: SiteSettingItem[]; grouped: Record<string, SiteSettingItem[]> }>(
      this.adminSettingsUrl,
    );
  }

  bulkUpdate(settings: Array<{ key: string; value: string | null }>): Observable<any> {
    return this.http.post(`${this.adminSettingsUrl}/bulk-update`, { settings });
  }

  uploadSettingAsset(file: File, settingKey?: string): Observable<{ path: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (settingKey) {
      formData.append('setting_key', settingKey);
    }
    return this.http.post<{ path: string }>(`${this.adminSettingsUrl}/upload-asset`, formData);
  }

  uploadSettingAssetWithProgress(
    file: File,
    settingKey?: string,
  ): Observable<HttpEvent<{ path: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    if (settingKey) {
      formData.append('setting_key', settingKey);
    }

    return this.http.post<{ path: string }>(`${this.adminSettingsUrl}/upload-asset`, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  updateSetting(key: string, value: string | null): Observable<any> {
    return this.http.post(`${this.adminSettingsUrl}/${key}`, { value });
  }

  createSetting(setting: {
    key: string;
    value: string | null;
    type: string;
    group: string;
    label?: string;
    description?: string;
  }): Observable<any> {
    return this.http.post(this.adminSettingsUrl, setting);
  }

  initializeDefaults(): Observable<any> {
    return this.http.post(`${this.adminSettingsUrl}/initialize-defaults`, {});
  }
}
